import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Checkbox } from '../../ui/checkbox';
import apiManutencao from '../../../services/apis/apiManutencao';
import { executarAcaoTarefa, mensagemErroAcao } from '../../manutencao/acoesTarefa';
import { AuthContext } from '../../../contexts/AuthContext';
import { ToastContext } from '../../../contexts/ToastContext';
import { getErrorMessage } from '../../../utils/errorMessage';
import { nomePeriodicidade } from '../../manutencao/periodicidades';
import FormConclusaoTarefa from '../../manutencao/FormConclusaoTarefa';

const INTERVALO_ATUALIZACAO_MS = 5 * 60 * 1000;

const AvisosManutencaoContext = createContext({ avisos: new Map(), recarregar: async () => {} });

const formatarData = (data) =>
    new Date(`${data}T00:00:00`).toLocaleDateString('pt-BR');

export function AvisosManutencaoProvider({ children }) {
    const [avisos, setAvisos] = useState([]);
    const canceladoRef = useRef(false);
    const intervaloRef = useRef(null);
    const { userInfo } = useContext(AuthContext);
    const liberado = Boolean(userInfo?.cargo);

    const carregar = useCallback(async () => {
        try {
            const res = await apiManutencao.getAvisosEquipamentos();
            if (!canceladoRef.current) setAvisos(res.data);
        } catch (error) {
            if (canceladoRef.current) return;
            if (error?.response?.status === 403) clearInterval(intervaloRef.current);
            setAvisos([]);
        }
    }, []);

    useEffect(() => {
        if (!liberado) {
            setAvisos([]);
            return;
        }
        canceladoRef.current = false;

        carregar();
        intervaloRef.current = setInterval(carregar, INTERVALO_ATUALIZACAO_MS);

        return () => {
            canceladoRef.current = true;
            clearInterval(intervaloRef.current);
        };
    }, [liberado, carregar]);

    const valor = useMemo(
        () => ({ avisos: new Map(avisos.map((a) => [a.tipoEquipamento, a])), recarregar: carregar }),
        [avisos, carregar]);

    return (
        <AvisosManutencaoContext.Provider value={valor}>
            {children}
        </AvisosManutencaoContext.Provider>
    );
}

export default function AvisoManutencao({ tipo, nome }) {
    const { avisos, recarregar } = useContext(AvisosManutencaoContext);
    const { erro } = useContext(ToastContext);
    const [aberto, setAberto] = useState(false);
    const [confirmandoId, setConfirmandoId] = useState(null);
    const [enviandoId, setEnviandoId] = useState(null);
    const fecharRef = useRef(null);

    useEffect(() => () => clearTimeout(fecharRef.current), []);

    const aviso = avisos.get(tipo);
    if (!aviso || aviso.tarefas.length === 0) return null;

    const atrasadas = aviso.tarefas.filter((t) => t.atrasada).length;

    const abrir = () => {
        clearTimeout(fecharRef.current);
        setAberto(true);
    };

    const alterarAberto = (valor) => {
        setAberto(valor);
        if (!valor) setConfirmandoId(null);
    };

    const agendarFechamento = () => {
        clearTimeout(fecharRef.current);
        if (confirmandoId != null) return;
        fecharRef.current = setTimeout(() => alterarAberto(false), 150);
    };

    const executarAcao = async (tarefa, acao, dados) => {
        if (enviandoId != null) return;
        setEnviandoId(tarefa.id);
        try {
            await executarAcaoTarefa(tarefa.id, acao, dados);
            setConfirmandoId(null);
        } catch (error) {
            erro(getErrorMessage(error, mensagemErroAcao(acao)));
        } finally {
            setEnviandoId(null);
            await recarregar();
        }
    };

    return (
        <Popover open={aberto} onOpenChange={alterarAberto}>
            <PopoverTrigger
                type="button"
                onMouseEnter={abrir}
                onMouseLeave={agendarFechamento}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Manutenção pendente em ${nome}`}
                className="inline-flex items-center mx-2 align-middle"
            >
                <FaExclamationTriangle className={`h-5 w-5 drop-shadow-1xl ${atrasadas > 0 ? 'text-red-500 animate-pulse' : 'text-yellow-400'}`} />
            </PopoverTrigger>
            <PopoverContent
                side="top"
                className="w-80 max-w-[90vw] p-3"
                onMouseEnter={abrir}
                onMouseLeave={agendarFechamento}
                onOpenAutoFocus={(e) => e.preventDefault()}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-2 mb-2">
                    <FaExclamationTriangle className={atrasadas > 0 ? 'text-red-500' : 'text-yellow-400'} />
                    <span className="font-semibold text-sm">Manutenção — {nome}</span>
                </div>
                <ul className="flex flex-col gap-2 max-h-64 overflow-y-auto scrollbar-thin scrollbar-track-primary scrollbar-thumb-darkaccent pr-1">
                    {aviso.tarefas.map((tarefa) => (
                        <li
                            key={tarefa.id}
                            className={`text-sm rounded px-2 py-1 border-l-4 ${tarefa.atrasada ? 'border-red-500 bg-red-900/30' : 'border-yellow-400 bg-gray-800/60'}`}
                        >
                            <div className="flex items-start gap-2">
                                <Checkbox
                                    className={`mt-0.5 ${tarefa.atrasada ? 'border-red-400' : ''}`}
                                    checked={confirmandoId === tarefa.id}
                                    disabled={enviandoId != null}
                                    onCheckedChange={() => setConfirmandoId(confirmandoId === tarefa.id ? null : tarefa.id)}
                                    aria-label="Concluir, reagendar ou ignorar tarefa"
                                />
                                <div className="flex-1 min-w-0">
                                    <p className={`whitespace-pre-wrap break-words ${tarefa.atrasada ? 'text-red-300' : ''}`}>{tarefa.acao}</p>
                                    <p className="text-xs text-gray-400 mt-0.5">
                                        {tarefa.nomeTabela} · {nomePeriodicidade(tarefa.periodicidade)}
                                        {tarefa.atrasada
                                            ? <span className="text-red-400 font-semibold"> · Venceu em {formatarData(tarefa.dataAviso)}</span>
                                            : ' · Vence hoje'}
                                    </p>
                                </div>
                            </div>
                            {confirmandoId === tarefa.id && (
                                <FormConclusaoTarefa
                                    compacto
                                    tarefa={tarefa}
                                    enviando={enviandoId === tarefa.id}
                                    onConfirmar={(acao, dados) => executarAcao(tarefa, acao, dados)}
                                    onCancelar={() => setConfirmandoId(null)}
                                />
                            )}
                        </li>
                    ))}
                </ul>
            </PopoverContent>
        </Popover>
    );
}
